// A comprehensive tracker for SEA (Search Engine Advertising) events and attribution

import { AcquisitionData, TrackingData, TrackingEventData } from './Types'
import { NextRouter } from 'next/router'
import { getFullUrlFromRouter } from '@/integrations/tracking/custom-sea-tracking/lib'
import graphQLClient from '@/lib/api/graphQLClient'

// Constants
const COOKIE_EXPIRY_DAYS = 30
const STORAGE_KEY_ACQUISITION = 'sea_acquisition_data'
const STORAGE_KEY_SESSION = 'sea_session_id'
const STORAGE_KEY_TRACKED_EVENTS = 'sea_tracked_events'

// Events that should only be tracked once per session
const SINGLE_TRACK_EVENTS = ['View_Tour', 'Download_PDF', 'Start_Checkout']

/**
 * Main tracker class for SEA events and attribution
 */
class SeaTracker {
  private static instance: SeaTracker
  private initialized: boolean = false
  private acquisitionData: AcquisitionData | null = null

  /**
   * Private constructor for singleton pattern
   */
  private constructor() {}

  /**
   * Returns the singleton instance of the tracker
   */
  public static getInstance(): SeaTracker {
    if (!SeaTracker.instance) {
      SeaTracker.instance = new SeaTracker()
    }
    return SeaTracker.instance
  }

  /**
   * Initializes the tracker and captures acquisition data
   * @param router Next.js router to get the full URL with query parameters
   */
  public init(router: NextRouter): void {
    if (this.initialized) {
      return
    }

    // Initialize acquisition data
    this.captureAcquisitionData(router)

    // Initialize session ID
    this.getSessionId()

    this.initialized = true
  }

  /**
   * Generates a unique session ID
   */
  private generateSessionId(): string {
    return 'sea_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15)
  }

  /**
   * Gets or creates a session ID
   */
  private getSessionId(): string {
    if (typeof window === 'undefined') return ''

    let sessionId = localStorage.getItem(STORAGE_KEY_SESSION) || this.getCookie(STORAGE_KEY_SESSION)

    if (!sessionId) {
      sessionId = this.generateSessionId()
      localStorage.setItem(STORAGE_KEY_SESSION, sessionId)
      this.setCookie(STORAGE_KEY_SESSION, sessionId, COOKIE_EXPIRY_DAYS)
    }

    return sessionId
  }

  /**
   * Sets a cookie with the given name, value and expiry days
   * Only sets the cookie if it doesn't already exist
   */
  private setCookie(name: string, value: string, expiryDays: number): void {
    if (typeof document === 'undefined') return

    // Check if cookie already exists
    const existingCookie = this.getCookie(name)
    if (existingCookie !== null) return // Skip setting cookie if it already exists

    const date = new Date()
    date.setTime(date.getTime() + expiryDays * 24 * 60 * 60 * 1000)
    const expires = 'expires=' + date.toUTCString()
    document.cookie = name + '=' + value + ';' + expires + ';path=/;SameSite=Lax'
  }

  /**
   * Gets a cookie value by name
   */
  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null

    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1)
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
  }

  /**
   * Determines source and medium based on referrer and URL parameters
   */
  private determineSourceAndMedium(router: NextRouter): { source: string; medium: string } {
    if (!router) return { source: 'error', medium: 'error' }

    const url = new URL(window.location.href)
    const referrer = document.referrer ? new URL(document.referrer) : null
    const referrerDomain = referrer ? referrer.hostname : ''

    // UTM parameters have highest priority
    const utmSource = (router.query.utm_source as string) || url.searchParams.get('utm_source')
    const utmMedium = (router.query.utm_medium as string) || url.searchParams.get('utm_medium')

    if (utmSource && utmMedium) {
      return { source: utmSource, medium: utmMedium }
    }

    // Check for click IDs
    const gclid = url.searchParams.get('gclid')
    const msclkid = url.searchParams.get('msclkid')
    const fbclid = url.searchParams.get('fbclid')
    const ttclid = url.searchParams.get('ttclid')
    const scclid = url.searchParams.get('scclid')
    const li_fat_id = url.searchParams.get('li_fat_id')

    // Google Ads (with gclid)
    if (gclid) {
      return { source: 'google', medium: 'cpc' }
    }

    // Microsoft Ads (with msclkid)
    if (msclkid) {
      return { source: 'microsoftads', medium: 'cpc' }
    }

    // Meta Ads (with fbclid)
    if (fbclid) {
      if (referrerDomain.includes('facebook.com')) {
        return { source: 'facebook', medium: 'paid' }
      } else if (referrerDomain.includes('instagram.com')) {
        return { source: 'instagram', medium: 'paid' }
      }
      return { source: 'facebook', medium: 'paid' } // Default to facebook if referrer unknown
    }

    // TikTok Ads (with ttclid)
    if (ttclid) {
      return { source: 'tiktok', medium: 'paid' }
    }

    // Snapchat Ads (with scclid)
    if (scclid) {
      return { source: 'snapchat', medium: 'paid' }
    }

    // LinkedIn Ads (with li_fat_id)
    if (li_fat_id) {
      return { source: 'linkedin', medium: 'paid' }
    }

    // Organic search engines
    if (referrerDomain.includes('google')) {
      return { source: 'google', medium: 'organic' }
    }

    if (referrerDomain.includes('bing')) {
      return { source: 'bing', medium: 'organic' }
    }

    if (referrerDomain.includes('yahoo.com')) {
      return { source: 'yahoo', medium: 'organic' }
    }

    if (referrerDomain.includes('duckduckgo.com')) {
      return { source: 'duckduckgo', medium: 'organic' }
    }

    if (referrerDomain.includes('baidu.com')) {
      return { source: 'baidu', medium: 'organic' }
    }

    // Social organic
    if (referrerDomain.includes('facebook.com')) {
      return { source: 'facebook', medium: 'organic' }
    }

    if (referrerDomain.includes('instagram.com')) {
      return { source: 'instagram', medium: 'organic' }
    }

    if (referrerDomain.includes('tiktok.com')) {
      return { source: 'tiktok', medium: 'organic' }
    }

    if (referrerDomain.includes('snapchat.com')) {
      return { source: 'snapchat', medium: 'organic' }
    }

    if (referrerDomain.includes('linkedin.com')) {
      return { source: 'linkedin', medium: 'organic' }
    }

    // Other referral
    if (referrerDomain && !referrerDomain.includes(window.location.hostname)) {
      return { source: referrerDomain, medium: 'referral' }
    }

    // Direct/none (default)
    return { source: 'direct', medium: 'none' }
  }

  /**
   * Captures and stores acquisition data based on the current visit
   */
  private captureAcquisitionData(router: NextRouter): void {
    if (typeof window === 'undefined') return

    // Check if we already have acquisition data stored
    const storedData = localStorage.getItem(STORAGE_KEY_ACQUISITION) || this.getCookie(STORAGE_KEY_ACQUISITION)
    if (storedData) {
      try {
        this.acquisitionData = JSON.parse(storedData)
        return // Use existing data if available
      } catch (e) {
        console.error('Error parsing stored acquisition data', e)
      }
    }

    const url = new URL(window.location.href)
    const { source, medium } = this.determineSourceAndMedium(router)

    // Create acquisition data object
    const acquisitionData: AcquisitionData = {
      source,
      medium,
      landing_page_url: getFullUrlFromRouter(router),
      session_id: this.getSessionId(),
      acquisition_timestamp: new Date().toISOString(),
    }

    // Add UTM parameters if present
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    utmParams.forEach((param) => {
      const value = url.searchParams.get(param)
      if (value) {
        acquisitionData[param as keyof AcquisitionData] = value
      }
    })

    // Add click IDs if present
    const clickIds = ['gclid', 'gbraid', 'msclkid', 'fbclid', 'ttclid', 'scclid', 'li_fat_id']
    clickIds.forEach((param) => {
      const value = url.searchParams.get(param)
      if (value) {
        acquisitionData[param as keyof AcquisitionData] = value
      }
    })

    // Store the acquisition data
    this.acquisitionData = acquisitionData
    localStorage.setItem(STORAGE_KEY_ACQUISITION, JSON.stringify(acquisitionData))
    this.setCookie(STORAGE_KEY_ACQUISITION, JSON.stringify(acquisitionData), COOKIE_EXPIRY_DAYS)

    //
  }

  /**
   * Checks if the tracker has been initialized
   */
  private checkInitialized(): void {
    if (!this.initialized) {
      console.warn('SeaTracker is not initialized. Please call init() first.')
    }
  }

  /**
   * Gets the current acquisition data
   */
  public getAcquisitionData(): AcquisitionData | null {
    this.checkInitialized()

    if (!this.acquisitionData) {
      // Try to get from localStorage or cookie
      const storedData = localStorage.getItem(STORAGE_KEY_ACQUISITION) || this.getCookie(STORAGE_KEY_ACQUISITION)
      if (storedData) {
        try {
          this.acquisitionData = JSON.parse(storedData)
        } catch (e) {
          console.error('Error parsing stored acquisition data', e)
        }
      }
    }

    return this.acquisitionData
  }

  /**
   * Prepares event data with acquisition information
   */
  private prepareEventData(eventName: string, additionalData?: TrackingData): TrackingEventData {
    const acquisitionData = this.getAcquisitionData()
    const timestamp = new Date().toISOString()

    // Base event data with camelCase keys
    const eventData: TrackingEventData = {
      conversionName: eventName,
      conversionTime: timestamp,
      tenantId: process.env.NEXT_PUBLIC_TENANT_ID!,
    }

    // Convert acquisition data from snake_case to camelCase
    if (acquisitionData) {
      Object.entries(acquisitionData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert snake_case to camelCase
          const camelKey = key.replace(/(_\w)/g, (m) => m[1].toUpperCase())
          eventData[camelKey] = value
        }
      })
    }

    // Convert additional data from snake_case to camelCase
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert snake_case to camelCase
          const camelKey = key.replace(/(_\w)/g, (m) => m[1].toUpperCase())
          eventData[camelKey] = value
        }
      })
    }

    return eventData
  }

  /**
   * Sends an event to the tracking system
   */
  private trackEvent(eventName: string, data?: TrackingData): void {
    this.checkInitialized()

    // Check if this event should be tracked based on session storage
    if (!this.shouldTrackEvent(eventName)) {
      return
    }

    const eventData = this.prepareEventData(eventName, data)

    // language=GraphQL
    const SEND_CONVERSION_EVENT = `mutation SendConversionEvent($input: CreateTrackingConversionInput) {
      createTrackingConversion(input: $input) {
        id
      }
    }`
    graphQLClient.request(SEND_CONVERSION_EVENT, { input: eventData }).then(() => {})
  }

  /**
   * Tracks viewing a tour
   * Conversion Name: "View_Tour"
   */
  public trackViewTour(tourName: string, data?: TrackingData): void {
    const eventData = {
      tour_name: tourName,
      ...data,
    }

    this.trackEvent('View_Tour', eventData)
  }

  /**
   * Tracks downloading a PDF
   * Conversion Name: "Download_PDF"
   */
  public trackDownloadPdf(tourName?: string, data?: TrackingData): void {
    const eventData = {
      tour_name: tourName,
      ...data,
    }

    this.trackEvent('Download_PDF', eventData)
  }

  /**
   * Tracks starting a checkout process
   * Conversion Name: "Start_Checkout"
   */
  public trackStartCheckout(tourName: string, currency?: string, value?: number, data?: TrackingData): void {
    const eventData = {
      tour_name: tourName,
      conversion_currency: currency,
      conversion_value: value,
      ...data,
    }

    this.trackEvent('Start_Checkout', eventData)
  }

  /**
   * Tracks a completed booking
   * Conversion Name: "Booking"
   */
  public trackBooking(tourName: string, currency: string, value: number, data?: TrackingData): void {
    const eventData = {
      tour_name: tourName,
      conversion_value: value,
      conversion_currency: currency,
      ...data,
    }
    this.trackEvent('Booking', eventData)
  }

  /**
   * Checks if an event should be tracked based on session storage
   */
  private shouldTrackEvent(eventName: string): boolean {
    // For single-track events, check if we have already tracked this event in the current session
    if (SINGLE_TRACK_EVENTS.includes(eventName)) {
      const trackedEvents = JSON.parse(localStorage.getItem(STORAGE_KEY_TRACKED_EVENTS) || '[]')
      // const eventKey = `${eventName}_${eventData.tour_name || ''}` // Create a unique key for the event
      const eventKey = eventName // Create a unique key for the event

      const alreadyTracked = trackedEvents.includes(eventKey)

      // If already tracked, skip tracking and return false
      if (alreadyTracked) {
        return false
      }

      // Otherwise, add the event to the tracked events and continue with tracking
      trackedEvents.push(eventKey)
      localStorage.setItem(STORAGE_KEY_TRACKED_EVENTS, JSON.stringify(trackedEvents))
    }

    return true // Event should be tracked
  }
}

export default SeaTracker.getInstance()
